import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Paper,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Lock as LockIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { FiArrowLeft } from "react-icons/fi";
import api from "../api/axiosConfig";

const AdminProfile = () => {
  const navigate = useNavigate();
  const [adminInfo, setAdminInfo] = useState(null);
  const [originalAdminInfo, setOriginalAdminInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [message, setMessage] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    fetchAdminInfo();
  }, []);

  const fetchAdminInfo = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin-login");
        return;
      }

      const response = await api.get("/api/users/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setAdminInfo(response.data);
      setOriginalAdminInfo(response.data);
    } catch (error) {
      console.error("Error fetching admin info:", error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/admin-login");
      } else {
        setMessage("Error loading profile information");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setOriginalAdminInfo({ ...adminInfo });
    setIsEditing(true);
    setMessage("");
  };

  const handleSave = async () => {
    if (!adminInfo.name || !adminInfo.username || !adminInfo.email) {
      setMessage("Name, username, and email are required.");
      return;
    }

    setIsSaving(true);
    setMessage("");

    try {
      const response = await api.put(
        `/api/users/me`,
        {
          name: adminInfo.name,
          username: adminInfo.username,
          email: adminInfo.email,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      setMessage("Profile updated successfully!");
      setIsEditing(false);
      setOriginalAdminInfo({ ...adminInfo });

      // Clear message after 3 seconds
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Profile update error:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to update profile";
      setMessage(errorMessage);

      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/admin-login");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setAdminInfo({ ...originalAdminInfo });
    setIsEditing(false);
    setMessage("");
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteDialog(false);

    try {
      await api.delete(`/api/users/me`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      localStorage.clear();
      navigate("/admin-login");
    } catch (error) {
      console.error("Profile deletion error:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete profile";
      setMessage(errorMessage);

      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/admin-login");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChangePassword = async () => {
    if (
      !passwordData.oldPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      setMessage("Please fill in all fields");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage("New password must be at least 6 characters long");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage("New passwords do not match");
      return;
    }

    if (passwordData.oldPassword === passwordData.newPassword) {
      setMessage("New password cannot be the same as current password");
      return;
    }

    setIsChangingPassword(true);
    setMessage("");

    try {
      const response = await api.post(
        "/api/users/me/change-password",
        {
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      setMessage(response.data.message || "Password changed successfully!");
      setChangePasswordDialog(false);
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // Clear message after 3 seconds
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Password change error:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to change password";
      setMessage(errorMessage);

      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin-login");
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      localStorage.clear();
      navigate("/admin-login");
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>Loading profile...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Button
          startIcon={<FiArrowLeft />}
          onClick={() => navigate("/admin-dashboard")}
          sx={{ mr: 2 }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" component="h1">
          Admin Profile
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {message && (
        <Alert
          severity={
            message.toLowerCase().includes("success") ? "success" : "error"
          }
          sx={{ mb: 3 }}
        >
          {message}
        </Alert>
      )}

      {/* Profile Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: "flex", alignItems: "center" }}
        >
          <PersonIcon sx={{ mr: 1 }} />
          Profile Information
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <TextField
              label="Name"
              value={adminInfo?.name || ""}
              onChange={(e) =>
                setAdminInfo({ ...adminInfo, name: e.target.value })
              }
              fullWidth
              margin="normal"
              disabled={!isEditing}
              required={isEditing}
              sx={{ mr: 1 }}
            />
            <IconButton
              onClick={isEditing ? handleSave : () => setIsEditing(true)}
              color={isEditing ? "primary" : "default"}
              disabled={isSaving}
            >
              {isEditing ? <SaveIcon /> : <EditIcon />}
            </IconButton>
            {isEditing && (
              <IconButton
                onClick={() => setAdminInfo({ ...originalAdminInfo })}
                color="secondary"
                disabled={isSaving}
              >
                <CancelIcon />
              </IconButton>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <TextField
              label="Username"
              value={adminInfo?.username || ""}
              onChange={(e) =>
                setAdminInfo({ ...adminInfo, username: e.target.value })
              }
              fullWidth
              margin="normal"
              disabled={!isEditing}
              required={isEditing}
              sx={{ mr: 1 }}
            />
            <IconButton
              onClick={isEditing ? handleSave : () => setIsEditing(true)}
              color={isEditing ? "primary" : "default"}
              disabled={isSaving}
            >
              {isEditing ? <SaveIcon /> : <EditIcon />}
            </IconButton>
            {isEditing && (
              <IconButton
                onClick={() => setAdminInfo({ ...originalAdminInfo })}
                color="secondary"
                disabled={isSaving}
              >
                <CancelIcon />
              </IconButton>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <TextField
              label="Email"
              value={adminInfo?.email || ""}
              onChange={(e) =>
                setAdminInfo({ ...adminInfo, email: e.target.value })
              }
              fullWidth
              margin="normal"
              type="email"
              disabled={!isEditing}
              required={isEditing}
              sx={{ mr: 1 }}
            />
            <IconButton
              onClick={isEditing ? handleSave : () => setIsEditing(true)}
              color={isEditing ? "primary" : "default"}
              disabled={isSaving}
            >
              {isEditing ? <SaveIcon /> : <EditIcon />}
            </IconButton>
            {isEditing && (
              <IconButton
                onClick={() => setAdminInfo({ ...originalAdminInfo })}
                color="secondary"
                disabled={isSaving}
              >
                <CancelIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Actions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Account Actions
        </Typography>

        <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<LockIcon />}
            onClick={() => setChangePasswordDialog(true)}
            sx={{ minWidth: 180 }}
          >
            Change Password
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialog(true)}
            disabled={isDeleting}
            sx={{ minWidth: 180 }}
          >
            Delete Profile
          </Button>

          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ minWidth: 180 }}
          >
            Logout
          </Button>
        </Box>
      </Paper>

      {/* Change Password Dialog */}
      <Dialog
        open={changePasswordDialog}
        onClose={() => setChangePasswordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            label="Current Password"
            type="password"
            value={passwordData.oldPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, oldPassword: e.target.value })
            }
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="New Password"
            type="password"
            value={passwordData.newPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, newPassword: e.target.value })
            }
            fullWidth
            margin="normal"
            required
            helperText="Must be at least 6 characters long"
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={passwordData.confirmPassword}
            onChange={(e) =>
              setPasswordData({
                ...passwordData,
                confirmPassword: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialog(false)}>Cancel</Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={isChangingPassword}
          >
            {isChangingPassword ? "Changing..." : "Change Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Profile Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Profile</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your profile? This action cannot be
            undone and will permanently remove your account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Profile"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminProfile;
